/**
 * control.builder.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Inject, Injectable } from "@angular/core";
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators
} from "@angular/forms";

import { StepTransformer, TransformerEffects }
    from "../algorithms/management/pipeline/step-transformer";
import { IntakeStep } from "../algorithms/structure/";
import { TypedData, TypedDataHolder, TypedDataHolderWithDefaults }
    from "../common/data/typed-data";
import { valueless } from "../support/utilities/functions";
import { ValidationBuilder } from "./validation.builder";

interface Controlling {
    control : AbstractControl;
}

export interface SizeChangeHandler
{
    extend (data : Controlling, value? : any) : void;
    remove (data : Controlling, index : number) : void;
}

// tslint:disable-next-line:max-classes-per-file
@Injectable()
export class IntakeControlBuilder
implements StepTransformer, SizeChangeHandler
{
    public readonly order : number = 100;

    public matchesType (type : any) : boolean
    {
        return type === IntakeStep;
    }

    public create (step : IntakeStep) : TransformerEffects
    {
        if (step.control instanceof AbstractControl)
        {
            throw new Error("Create called on step with control!");
        }

        if (!TypedData.applyDefaults(step))
        {
            console.error(step);
            throw new Error("Could not apply defaults to data");
        }

        if (step.cardinality === 1
            || (step.restrictions.length > 0 && !step.allowsDuplicates))
        {
            this.createSingular(step);
        }
        else if (step.cardinality === 0)
        {
            this.createVariable(step);
        }
        else
        {
            this.createPlural(step, step.cardinality);
        }

        return {control_created: true};
    }

    private isDisabled (data : TypedDataHolderWithDefaults) : boolean
    {
        return (data.hints && data.hints.editable === false);
    }

    private createSingular (data : TypedDataHolderWithDefaults) : void
    {
        data.control = new FormControl(
            {value: data.value, disabled: this.isDisabled(data)},
            ValidationBuilder.for(data)
		);
    }
    private createVariable (data : TypedDataHolderWithDefaults) : void
    {
        if (Array.isArray(data.value) && data.value.length > 0)
        {
            // if we already have data of a certain size, we make as many fields
            // as we have values
            return this.createPlural(data, data.value.length);
        }
        // otherwise just start with one (empty) field
        this.createPlural(data, 1);
    }
    private createPlural (data : TypedDataHolderWithDefaults, size : number)
        : void
    {
        const fields : AbstractControl[] = [];
        const values : any[] = data.value || [];
        const validators : ValidatorFn[] =
            ValidationBuilder.for(data);
        const disabled : boolean = this.isDisabled(data);
        for (let i : number = 0; i < size; i++)
        {
            const value : any = (values[i] === undefined) ? null : values[i];
            fields.push(new FormControl(
                {value, disabled},
                validators
            ));
        }
        data.control = new FormArray(fields);
    }

    public extend (
        data : Controlling | TypedDataHolder,
        value? : any,
        validators? : ValidatorFn[]
    ) : void
    {
        if (data instanceof IntakeStep && !validators)
        {
            validators = ValidationBuilder.for(data);
        }
        if (data.control instanceof FormArray)
        {
            data.control.push(new FormControl(value || null, validators));
        }
        else
        {
            console.error(data);
            throw new Error("Can only extend controls backed by FormArray");
        }
    }

    public remove (data : Controlling, index : number) : void
    {
        if (data.control instanceof FormArray)
        {
            index = index % data.control.length;
            data.control.removeAt(index);
        }
        else
        {
            console.error(data);
            throw new Error("Can only remove fields from a FormArray control");
        }
    }

    // tslint:disable-next-line:cyclomatic-complexity
    public apply (data : IntakeStep) : TransformerEffects
    {
        if (!(data.control instanceof AbstractControl))
        {
            console.warn("data has no control", data.control);
            return {};
        }

        const flags : TransformerEffects = {};
        if (data.control instanceof FormArray)
        {
            if (valueless(data.value) || data.value.length === 0)
            {
                // We want to preserve at least one subfield. Autosize already
                // sets a minimum size of 1, but if the sizes of value and the
                // FormArray don't match, setValue will throw an error.
                // (We could use the forgiving `patchValue`, but we'd otherwise
                // like to detect size/structure mismatches.)
                data.value = [null];
                flags.coerced_null_value = true;
            }
            if (!Array.isArray(data.value))
            {
                console.error(data);
                throw new Error("Cannot apply non-array data to a FormArray!");
            }
            if (data.cardinality === 0)
            {
                flags.array_resized =
                    this.autosize(data.control, data.value, data);
            }
        }
        try
        {
            data.control.setValue(data.value, {emitEvent: false});
        }
        catch (err)
        {
            console.error(
                "setValue encountered an array size mismatch!",
                data, data.value, err
            );
            data.control.patchValue(data.value, {emitEvent: false});
        }

        if (data.hints._group)
        {
            data.hints._group.autosize(true);
        }
        return flags;
    }

    private autosize (control : FormArray, value : any[], data : IntakeStep)
        : number
    {
        const targetSize : number = Math.max(1, value.length);
        const delta : number = targetSize - control.length;
        while (control.length > targetSize)
        {
            this.remove({control}, -1);
        }
        while (control.length < targetSize)
        {
            this.extend({control});
        }
        return delta;
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/control.builder.ts